import React from "react";
import { InsightCards, KpiTile, Stat } from "./StatsTiles";
import { DEFINITIONS } from "./courtStatsDefinitions";
import { computeDelta, fmtHours, fmtInt, fmtLead, fmtMoney, fmtMoneyCompact, fmtNumber1, fmtPct, fmtPct0 } from "./statsFormat";
import { buildSeries, deriveMetrics, selectInsights, sparkValues } from "./courtStatsMetrics";

/**
 * Resumen tab: four headline tiles (with sparkline and change vs the comparison period), the
 * rates strip (cancellations, no-shows, unsold hours, lead time) and up to three insights.
 *
 * @param {{current: object, comparison: object|null, compareLabel: string, bucket: number}} props
 *   `current` / `comparison` are stats responses; `bucket` is the sparkline grouping (1 or 7 days).
 */
export default function ResumenTab({ current, comparison, compareLabel, bucket }) {
  const m = deriveMetrics(current);
  const c = deriveMetrics(comparison);
  const spark = (metric) => sparkValues(buildSeries(current.daily, metric, bucket));
  const insights = selectInsights(current);
  const unknownAmounts = current.totals.amountUnknownReservations;

  return (
    <>
      <section className="stats-kpis" aria-label="Indicadores principales">
        <KpiTile
          icon="occ"
          definition={DEFINITIONS.occ}
          label="Ocupación"
          value={fmtPct(m.occupancy)}
          delta={computeDelta("pp", m.occupancy, c?.occupancy, true)}
          compareLabel={compareLabel}
          sub={`${fmtHours(m.bookedHours)} reservadas de ${fmtHours(m.availableHours)}`}
          spark={spark("occ")}
        />
        <KpiTile
          icon="rev"
          definition={DEFINITIONS.rev}
          label="Ingresos cobrados"
          value={fmtMoneyCompact(m.revenue)}
          delta={computeDelta("pct", m.revenue, c?.revenue, true)}
          compareLabel={compareLabel}
          sub={`Pendiente de cobro: ${fmtMoney(m.pending)}`}
          spark={spark("rev")}
        />
        <KpiTile
          icon="res"
          definition={DEFINITIONS.res}
          label="Reservas"
          value={fmtInt(m.reservations)}
          delta={computeDelta("pct", m.reservations, c?.reservations, true)}
          compareLabel={compareLabel}
          sub={`${fmtNumber1(m.perDay)} por día en promedio`}
          spark={spark("res")}
        />
        <KpiTile
          icon="iph"
          definition={DEFINITIONS.iph}
          label="Ingreso por hora disponible"
          value={fmtMoney(m.revenuePerHour)}
          delta={computeDelta("pct", m.revenuePerHour, c?.revenuePerHour, true)}
          compareLabel={compareLabel}
          sub="Ingresos ÷ horas que la cancha estuvo abierta"
          spark={spark("iph")}
        />
      </section>

      <section className="stats-strip" aria-label="Cancelaciones, ausencias y capacidad">
        <Stat
          definition={DEFINITIONS.canc}
          label="Cancelaciones"
          value={fmtPct(m.cancellationRate)}
          delta={computeDelta("pp", m.cancellationRate, c?.cancellationRate, false)}
          compareLabel={compareLabel}
          sub={`${fmtInt(m.cancelled)} reservas canceladas`}
        />
        <Stat
          definition={DEFINITIONS.ns}
          label="Ausencias"
          value={fmtPct(m.noShowRate)}
          delta={computeDelta("pp", m.noShowRate, c?.noShowRate, false)}
          compareLabel={compareLabel}
          sub={`Sin cobrar: ${fmtMoney(m.noShowUncollected)}`}
        />
        <Stat
          definition={DEFINITIONS.unsold}
          label="Horas sin vender"
          value={fmtHours(m.unsoldHours)}
          delta={computeDelta("pct", m.unsoldHours, c?.unsoldHours, false)}
          compareLabel={compareLabel}
          sub={`de ${fmtHours(m.availableHours)} disponibles`}
        />
        <Stat
          definition={DEFINITIONS.lead}
          label="Anticipación promedio"
          value={fmtLead(m.avgLeadHours)}
          delta={computeDelta("lead", m.avgLeadHours, c?.avgLeadHours, null)}
          compareLabel={compareLabel}
          sub={`${fmtPct0(m.sameDayShare)} reserva el mismo día`}
        />
      </section>

      <InsightCards insights={insights} />

      {unknownAmounts > 0 && (
        <p className="stats-footnote">
          {fmtInt(unknownAmounts)} {unknownAmounts === 1 ? "reserva no tiene" : "reservas no tienen"} el total registrado, así que{" "}
          {unknownAmounts === 1 ? "no suma" : "no suman"} a lo pendiente de cobro ni a lo que se perdió por ausencias.
        </p>
      )}
    </>
  );
}
